/**
 * report-viewer-scope (US-E23.F1-0867 · @UC0048 · QĐ PO 30/09/2026) — TẦM NHÌN của người xuất báo cáo PDF.
 *
 * *"Ai cũng xuất được sổ thu chi ⇒ minh bạch"*: MỌI thành viên xuất được PDF `finance`, và **nội dung tệp = đúng những gì
 * người đó thấy trong app**. O/A/T xuất đủ như trước (`audience = managers`, 0 thay đổi); member thường nhận bản đã lọc.
 *
 * ═══ AI QUYẾT, AI ÁP ═══
 *  · **API QUYẾT** lúc tạo job (`ReportViewerScopeService`), bằng CHÍNH các hàm app đang dùng cho từng màn —
 *    `maySeeMemberContribution` · `groupMoneyTotalsVisible` · `FundConfigService` · `resolveActorRepresentedSeatIdsInGroup`.
 *    Kết quả chụp vào `report_jobs.viewerScope` (schema dưới). Không tin client: mọi mảnh lấy từ guard + DB.
 *  · **Worker chỉ ÁP** (`report-pdf.viewer-scope.ts`): đổi số bị ẩn thành `REPORT_AMOUNT_REDACTED` TRƯỚC khi dựng tài liệu —
 *    số thật không vào `FinanceReportDocument`, nên không có đường nào in nó ra.
 *  · Ngoại lệ DUY NHẤT quyết ở worker: `resolveVisibility` theo TỪNG khoản chi (một resolver, ở ngay `visibility-scope.ts`) —
 *    chụp trước quyết định của hàng nghìn khoản chi là vô nghĩa khi resolver đã thuần và nằm sẵn ở đây.
 *
 * 🔴 `'•••'`, KHÔNG `0` (CLAUDE.md §3.8): `0` in lên giấy là lời khẳng định sai về tiền — "người này chưa đóng đồng nào".
 * 🔴 FAIL-CLOSED: quỹ không có trong tập đã đánh giá (sinh SAU lúc chụp) ⇒ ẩn; `viewerScope` hỏng ⇒ job `failed`.
 *
 * ⚠️ ĐÃ BIẾT, CÓ CHỦ ĐÍCH (review #1077 M1 — câu hỏi PO): mỗi phần ẩn THEO LUẬT CỦA MÀN APP TƯƠNG ỨNG, không theo "cả tờ
 * giấy". Nên một số bị ẩn ở phần này có thể SUY RA từ số được hiện ở phần khác — vd. thẻ "Tổng chi" (luật thẻ nhóm: mọi quỹ
 * public) trừ các dòng §1 đang hiện = khoản `sensitive` bị ẩn; tổng §3.1 (luật "ai đã đóng") = tổng §2 bị ẩn theo
 * `collection_amount_visibility`. App lộ y hệt (thẻ nhóm + lịch sử giao dịch hiện cả hai; Bảng thu hiện tổng đợt cho mọi
 * member) ⇒ PDF KHÔNG rộng hơn app. Muốn chặt hơn app (ẩn cả phần suy ra được) là một QĐ PO riêng — đổi ở đây, không ở worker.
 */
import { z } from "zod";

/** Số bị ẩn — CÙNG literal với `GROUP_MONEY_REDACTED`/`REDACTED_PLACEHOLDER` của contracts (một placeholder cho mọi bề mặt). */
export const REPORT_AMOUNT_REDACTED = "•••" as const;
export type ReportAmountRedacted = typeof REPORT_AMOUNT_REDACTED;

/** Số tiền trên báo cáo: BigInt minor khi được xem, `'•••'` khi bị ẩn. KHÔNG có nhánh `number`/`0`. */
export type ReportAmount = bigint | ReportAmountRedacted;

export function isReportAmountRedacted(v: ReportAmount): v is ReportAmountRedacted {
  return v === REPORT_AMOUNT_REDACTED;
}

/** Σ — MỘT vế ẩn ⇒ tổng ẩn (tổng lộ ra thì trừ đi phần hiện là suy lại được phần ẩn). */
export function sumReportAmounts(values: readonly ReportAmount[]): ReportAmount {
  let total = 0n;
  for (const v of values) {
    if (isReportAmountRedacted(v)) return REPORT_AMOUNT_REDACTED;
    total += v;
  }
  return total;
}

/** a − b — một vế ẩn ⇒ hiệu ẩn. */
export function subtractReportAmounts(a: ReportAmount, b: ReportAmount): ReportAmount {
  if (isReportAmountRedacted(a) || isReportAmountRedacted(b)) return REPORT_AMOUNT_REDACTED;
  return a - b;
}

/**
 * Khán giả của một job — mirror CHECK `report_jobs_audience_check`.
 *  · `managers` — O/A/T lúc bấm (và MỌI job cũ: trước 0867 cả ba cửa tạo job đều gác O/A/T) ⇒ bản đầy đủ.
 *  · `member`   — member thường ⇒ bản theo `viewerScope`. CHECK DB ép kèm `viewerScope` + `requestedByUserId` + `finance`.
 */
export const REPORT_AUDIENCE = { MANAGERS: "managers", MEMBER: "member" } as const;
export type ReportAudience = (typeof REPORT_AUDIENCE)[keyof typeof REPORT_AUDIENCE];

/**
 * Kind DUY NHẤT member thường xuất được (QĐ PO 30/09: "PDF báo cáo thu chi"). CSV (`transactions`/`members`/`activity`) và hai
 * PDF tách cũ (`income`/`expense`) GIỮ O/A/T — mirror CHECK `report_jobs_member_audience_check` + cổng ở `ReportsService`.
 */
export const MEMBER_AUDIENCE_REPORT_KIND = "finance" as const;

const uuid = z.string().uuid();

/**
 * Tầm nhìn đã chụp của MỘT member. `.strict()` — trường lạ ⇒ parse hỏng ⇒ job `failed` (không đoán nghĩa trường mới).
 *
 * Mỗi trường mang tên MÀN app mà nó chép luật — đọc cùng bảng đối chiếu ở `index.md` §2 của task 0867.
 */
export const reportViewerScopeSchema = z
  .object({
    v: z.literal(1),
    /** Chỉ member thường có bản lọc — O/A/T không có `viewerScope`. Cho `resolveVisibility` (owner mới thấy `private`). */
    viewerRole: z.literal("member"),
    /** Ghế guard chọn lúc bấm (`requestedBy`) — `viewerMemberId` của `resolveVisibility`, y như `expense-read` dùng ghế của guard. */
    viewerMemberId: uuid,
    /** Người bấm — vế "người tạo khoản chi là bên liên quan" so theo user (khoản chi lưu `createdBy` là USER id). */
    viewerUserId: uuid,
    /** MỌI ghế sống người bấm đại diện (`resolveActorRepresentedSeatIdsInGroup`) — số của CHÍNH MÌNH luôn thấy. */
    selfSeatIds: z.array(uuid),
    /** Quỹ đang hoạt động lúc chụp. Quỹ ngoài tập này ⇒ coi như ẩn (fail-closed cho quỹ sinh sau). */
    evaluatedFundIds: z.array(uuid),
    /** 3 thẻ số — luật thẻ nhóm `GET /groups/:id` (`groupMoneyTotalsVisible`: MỌI quỹ `transparency_mode = public`). */
    groupTotalsVisible: z.boolean(),
    /** §1 — quỹ được xem SỐ khoản chi (`transparency_mode = public`; tab Hoạt động / lịch sử giao dịch). */
    moneyFundIds: z.array(uuid),
    /** §2 — quỹ được xem tổng đã thu của đợt (`collection_amount_visibility = all_members`; màn tiến độ đợt). */
    collectionAmountFundIds: z.array(uuid),
    /**
     * §3 — số của NGƯỜI KHÁC + danh sách "ai còn thiếu" (`maySeeMemberContribution` với `isSelf=false`, tức MỌI quỹ
     * `payment_status_visibility = all_members`; màn Thành viên · Bảng thu · Lịch sử đóng góp · kênh XEM P5.3).
     */
    othersDuesVisible: z.boolean(),
  })
  .strict();
export type ReportViewerScope = z.infer<typeof reportViewerScopeSchema>;
